import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SubsidyChoices } from "@/features/cart/types/cart.type";
import { DEALS_CART_QUERY_KEY } from "@/features/deals/constants/dealsCart.constants";
import { dealsCartService } from "@/features/deals/services/dealsCart.service";
import type { DealCart } from "@/features/deals/types/dealCart.type";
import type { AxiosError } from "axios";

/**
 * Extract error messages from API response
 */
const extractErrors = (data: unknown): string[] => {
  if (!data || typeof data !== "object") {
    return [];
  }

  const errors = (data as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors
      .map((msg) => (typeof msg === "string" ? msg : String(msg)))
      .filter(Boolean);
  }

  const message = (data as { message?: unknown }).message;
  if (typeof message === "string" && message.trim()) {
    return [message];
  }

  return [];
};

type DealsCartMutationOptions = { showToasts?: boolean };

type AddToDealCartPayload = {
  referenceId: string
  quantity: number
};

type UpdateDealCartItemPayload = {
  itemId: string
  quantity: number
};

type RemoveDealCartItemPayload = { referenceId: string };

/**
 * Hook for deals cart mutations (add, update, remove items)
 */
export const useDealsCartMutations = (options?: DealsCartMutationOptions) => {
  const showToasts = options?.showToasts ?? true;
  const queryClient = useQueryClient();

  const addToCart = useMutation({
    mutationFn: ({ referenceId, quantity }: AddToDealCartPayload) =>
      dealsCartService.addToCart(referenceId, quantity),
    onSuccess: (data: DealCart) => {
      queryClient.setQueryData(DEALS_CART_QUERY_KEY, data);
      const friendlyErrors = extractErrors(data);
      if (friendlyErrors.length) {
        if (showToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showToasts) {
        toast.success("Bon plan ajouté au panier");
      }
    },
    onError: (error: AxiosError) => {
      const friendlyErrors = extractErrors(error.response?.data);
      if (friendlyErrors.length) {
        if (showToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showToasts) {
        toast.error("Impossible d'ajouter ce bon plan au panier");
      }
    },
  });

  const updateCartItem = useMutation({
    mutationFn: ({ itemId, quantity }: UpdateDealCartItemPayload) =>
      dealsCartService.updateCartItem(itemId, quantity),
    onSuccess: (data: DealCart) => {
      queryClient.setQueryData(DEALS_CART_QUERY_KEY, data);
      if (showToasts) {
        toast.success("Panier mis à jour");
      }
    },
    onError: (error: AxiosError) => {
      const friendlyErrors = extractErrors(error.response?.data);
      if (friendlyErrors.length) {
        if (showToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showToasts) {
        toast.error("Impossible de mettre à jour le panier");
      }
    },
  });

  const removeCartItem = useMutation({
    mutationFn: ({ referenceId }: RemoveDealCartItemPayload) =>
      dealsCartService.removeCartItem(referenceId),
    onSuccess: (data: DealCart) => {
      queryClient.setQueryData(DEALS_CART_QUERY_KEY, data);
      if (showToasts) {
        toast.success("Bon plan retiré du panier");
      }
    },
    onError: (error: AxiosError) => {
      const friendlyErrors = extractErrors(error.response?.data);
      if (friendlyErrors.length) {
        if (showToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showToasts) {
        toast.error("Impossible de retirer ce bon plan du panier");
      }
    },
  });

  const clearCart = useMutation({
    mutationFn: () => dealsCartService.clearCart(),
    onSuccess: () => {
      // Immediately clear the cache for instant UI update
      // Set to empty cart structure instead of undefined for proper reactivity
      queryClient.setQueryData<DealCart | null>(DEALS_CART_QUERY_KEY, null);
      if (showToasts) {
        toast.success("Panier vidé");
      }
    },
    onError: () => {
      if (showToasts) {
        toast.error("Impossible de vider le panier");
      }
    },
  });

  const updateSubsidyChoices = useMutation({
    mutationFn: (payload: SubsidyChoices) =>
      dealsCartService.updateSubsidyChoices(payload),
    onMutate: async (newChoices: SubsidyChoices) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: DEALS_CART_QUERY_KEY });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<DealCart>(DEALS_CART_QUERY_KEY);

      // Optimistically update subsidy_choices
      if (previousCart) {
        queryClient.setQueryData<DealCart>(DEALS_CART_QUERY_KEY, {
          ...previousCart,
          subsidy_choices: newChoices,
        });
      }

      return { previousCart };
    },
    onSuccess: (data: DealCart) => {
      // Replace cache with full server response (includes recalculated totals)
      queryClient.setQueryData(DEALS_CART_QUERY_KEY, data);
    },
    onError: (error: AxiosError, _variables, context) => {
      // Rollback to previous cart state
      if (context?.previousCart) {
        queryClient.setQueryData(DEALS_CART_QUERY_KEY, context.previousCart);
      }

      const friendlyErrors = extractErrors(error.response?.data);
      if (friendlyErrors.length) {
        if (showToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showToasts) {
        toast.error("Impossible de mettre à jour les subventions");
      }
    },
  });

  return {
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    updateSubsidyChoices,
  };
};
